export function SaleAsIsLowerCosts() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-800 px-6 py-2 rounded-full font-bold text-sm mb-4">
            BENEFIT #6
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sell "As-Is" with Significantly Lower Costs
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Avoid expensive repairs, inspections, and bank-related closing fees while selling your property in its current condition
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/ec701ccd5a70495d84d9682c46afb34c.jpg"
              alt="Apartment building sold as-is without costly repairs"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              No Repairs, No Inspections, No Bank Fees
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Traditional bank financing requires properties to meet strict condition standards. Banks demand professional inspections, appraisals, environmental assessments, and often require sellers to complete repairs before approving loans. These requirements can cost <strong>tens of thousands of dollars</strong> and delay sales for months.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              With seller financing, you can <strong>sell your property "as-is"</strong> in its current condition. Buyers who use seller financing are typically experienced investors who understand property conditions and are willing to handle repairs themselves. This means you avoid the need for costly pre-sale repairs, extensive inspections, or meeting rigid bank property standards.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Additionally, seller-financed transactions eliminate many expensive bank-related closing costs such as loan origination fees, underwriting fees, processing fees, and other lender charges. Both parties save money, making the transaction more attractive and profitable for everyone involved.
            </p>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Costs You Can Avoid:</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Property Repairs:</strong> $10,000 - $100,000+ depending on condition
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Professional Inspections:</strong> $500 - $2,000 per inspection type
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Bank Appraisal:</strong> $2,000 - $5,000 for commercial properties
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Environmental Assessment:</strong> $2,000 - $10,000+
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Loan Origination Fees:</strong> 1-2% of loan amount ($20,000 - $40,000 on $2M)
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Bank Processing Fees:</strong> $1,000 - $5,000+
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Cost Comparison: Traditional Sale vs. Seller Financing
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900">Traditional Bank Sale</h4>
              </div>
              <div className="space-y-3 text-lg">
                <div className="flex justify-between pb-2 border-b border-red-200">
                  <span className="text-gray-700">Property Repairs:</span>
                  <span className="font-bold text-red-600">$35,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-red-200">
                  <span className="text-gray-700">Inspections:</span>
                  <span className="font-bold text-red-600">$3,500</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-red-200">
                  <span className="text-gray-700">Appraisal:</span>
                  <span className="font-bold text-red-600">$3,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-red-200">
                  <span className="text-gray-700">Environmental:</span>
                  <span className="font-bold text-red-600">$5,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-red-200">
                  <span className="text-gray-700">Loan Origination (1.5%):</span>
                  <span className="font-bold text-red-600">$30,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-red-200">
                  <span className="text-gray-700">Processing Fees:</span>
                  <span className="font-bold text-red-600">$3,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-red-200">
                  <span className="text-gray-700">Other Bank Fees:</span>
                  <span className="font-bold text-red-600">$2,500</span>
                </div>
                <div className="flex justify-between pt-3 text-2xl border-t-2 border-red-400">
                  <span className="font-bold">Total Costs:</span>
                  <span className="font-bold text-red-600">$82,000</span>
                </div>
              </div>
            </div>

            <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-hand-coin-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900">Seller Financing Sale</h4>
              </div>
              <div className="space-y-3 text-lg">
                <div className="flex justify-between pb-2 border-b border-teal-200">
                  <span className="text-gray-700">Property Repairs:</span>
                  <span className="font-bold text-teal-600">$0</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-teal-200">
                  <span className="text-gray-700">Inspections:</span>
                  <span className="font-bold text-teal-600">$0</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-teal-200">
                  <span className="text-gray-700">Appraisal:</span>
                  <span className="font-bold text-teal-600">$0</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-teal-200">
                  <span className="text-gray-700">Environmental:</span>
                  <span className="font-bold text-teal-600">$0</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-teal-200">
                  <span className="text-gray-700">Loan Origination:</span>
                  <span className="font-bold text-teal-600">$0</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-teal-200">
                  <span className="text-gray-700">Processing Fees:</span>
                  <span className="font-bold text-teal-600">$0</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-teal-200">
                  <span className="text-gray-700">Basic Legal/Title:</span>
                  <span className="font-bold text-teal-600">$3,000</span>
                </div>
                <div className="flex justify-between pt-3 text-2xl border-t-2 border-teal-400">
                  <span className="font-bold">Total Costs:</span>
                  <span className="font-bold text-teal-600">$3,000</span>
                </div>
              </div>
              <div className="mt-6 text-center bg-teal-600 text-white rounded-lg p-4">
                <div className="text-3xl font-bold">$79,000 Saved</div>
                <div className="text-teal-100">96% Cost Reduction</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Perfect for Properties That Need Work
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Seller financing is particularly advantageous for apartment buildings that need repairs, renovations, or updates. Banks typically won't finance properties with deferred maintenance, code violations, or significant repair needs. This forces sellers to either invest heavily in repairs before selling or accept drastically reduced offers from cash buyers.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              With seller financing, you can sell to experienced investors who specialize in value-add properties. These buyers understand the property's potential, have the expertise to handle renovations, and are willing to purchase as-is. You avoid the time, expense, and hassle of repairs while still achieving a fair price for your property.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-md">
              <h4 className="text-xl font-bold text-gray-900 mb-3">As-Is Sale Benefits</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>No need to invest in repairs before selling</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Sell properties with deferred maintenance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Avoid dealing with contractor delays and issues</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>No risk of repair costs exceeding estimates</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Faster time to closing without repair delays</span>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <img
              src="https://readdy.ai/api/search-image?query=cost%20savings%20concept%20with%20calculator%20showing%20reduced%20expenses%20money%20saved%20from%20avoiding%20repairs%20and%20bank%20fees%20financial%20documents%20with%20lower%20costs%20highlighted%20clean%20professional%20background%20business%20savings&width=800&height=600&seq=lower-costs-savings&orientation=landscape"
              alt="Cost savings from selling as-is with seller financing"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}